import { Typography } from '@mui/material'
import { WarningRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import SmartMateriaUITable from '../../../generic/SmartMateriaUITable'
import { CASCADE_LEVEL, CASCADE_LEVEL_LABEL } from '../cascadeApproval'

/**
 * Enmienda PO 23/07/2026 (HU-04/HU-05): shared confirmation modal for every index cascade
 * (workflow header, jobReference selector and save dialog). Shows the edited subject with its
 * previous and new index value, and the children that will change in a SmartMateriaUITable (the
 * amount of records can be large), identifying for each featureGroup the job and the task it
 * belongs to.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string} props.subjectLabel Label of the edited element (e.g. "Workflow WF-1 name")
 * @param {string} props.previousIndexValueName
 * @param {string} props.targetIndexValueName
 * @param {import('../cascadeApproval').CascadePendingItem[]} props.items Children that will change
 * @param {Function} props.onAccept
 * @param {Function} props.onCancel
 */
const CascadeIndexChangeDialog = ({ open, subjectLabel, previousIndexValueName, targetIndexValueName, items, onAccept, onCancel }) => {
  const { t_ } = useSmartTranslation()

  const columns = [
    {
      name: t_('Type'),
      field: 'levelLabel',
      readOnly: true
    },
    {
      name: t_('Code'),
      field: 'code',
      readOnly: true
    },
    {
      name: t_('Name'),
      field: 'name',
      readOnly: true
    },
    {
      name: t_('Job'),
      field: 'jobCode',
      readOnly: true
    },
    {
      name: t_('Task'),
      field: 'taskName',
      readOnly: true
    },
    {
      name: t_('Index'),
      field: 'indexValueName',
      readOnly: true
    }
  ]

  // Story 186 (RN49): the cascade never blocks; the feature groups still in DRAFT are only pointed out.
  const draftFeatureGroups = targetIndexValueName === 'DRAFT'
    ? []
    : items.filter((item) => item.level === CASCADE_LEVEL.FEATURE_GROUP && item.indexValueName === 'DRAFT')

  const dataFetch = () => items.map((item) => ({
    ...item,
    levelLabel: t_(CASCADE_LEVEL_LABEL[item.level] || ''),
    indexValueName: t_(item.indexValueName)
  }))

  return (
    <SmartDialog
      setOpen={open}
      title={createDialogTitle({ title: t_('Pending index changes'), icon: WarningRounded, color: 'warning' })}
      accept
      cancel
      acceptCallback={onAccept}
      cancelCallback={onCancel}
      renderComponent={
        <div data-testid='cascade-index-change-dialog'>
          <Typography sx={{ mb: 1 }}>
            {`${subjectLabel}: ${t_('Previous state')} ${t_(previousIndexValueName || '')} - ${t_('New state')} ${t_(targetIndexValueName || '')}`}
          </Typography>
          {draftFeatureGroups.length > 0 && (
            <div data-testid='cascade-draft-feature-groups'>
              <Typography sx={{ mb: 1 }} color='warning.main'>
                {t_('These feature groups are in {{state}} and will change to {{target}}', { state: t_('DRAFT'), target: t_(targetIndexValueName || '') })}
              </Typography>
              {draftFeatureGroups.map((item) => (
                <Typography key={item.id} sx={{ ml: 2 }}>{item.code}</Typography>
              ))}
            </div>
          )}
          <Typography sx={{ mb: 1 }}>
            {t_('The following items have a different index value. Accepting will change them all to the selected value.')}
          </Typography>
          {/* Not `pageable`: that mode expects a server-paginated {content, totalElements}
              response — with a local array the table paginates client-side via tablePageSize.
              `forceLoad` re-fetches whenever the items change (the dialog re-opens). */}
          <SmartMateriaUITable
            columns={columns}
            dataFetch={dataFetch}
            dense
            toolbarDisplay='none'
            tablePageSize={10}
            forceLoad={items}
          />
        </div>
      }
    />
  )
}

export default CascadeIndexChangeDialog
